import classNames from "classnames/bind";
import React, { useState } from "react";
import { Button } from "../Button";
import { IconLoading } from "../Icon";
import { Skeleton } from "../Skeleton";
import { useMediaBreakpoint } from "../utils/hooks/useMediaBreakpoint";
import styles from "./PanelCollapsible.module.scss";
import type { PanelCollapsibleProps } from "./types";

const cx = classNames.bind(styles);

/**
 * A panel that can have its content collapsed or expanded on-demand
 */
export const PanelCollapsible = ({
  children,
  title,
  titleDecorator,
  subtitle,
  description,
  actionsSlot,
  isCollapsed: isCollapsedProp = false,
  onCollapsedChange,
  canScrollAreaShrink = false,
  isLoading,
  isBusy,
  className,
  style,
}: PanelCollapsibleProps) => {
  const [isCollapsed, setCollapsed] = useState(isCollapsedProp);
  const { currentBreakpointWidth, allBreakpoints } = useMediaBreakpoint();

  const handleOnCollapse = (newState: boolean) => {
    if (onCollapsedChange) {
      onCollapsedChange(newState);
      return;
    }

    setCollapsed(newState);
  };

  return (
    <Skeleton
      isLoading={isLoading}
      isBusy={isBusy}
    >
      <div
        className={cx("container", isCollapsed && "collapsed", className)}
        style={style}
        aria-expanded={!isCollapsed}
      >
        {isCollapsed ? (
          <div
            className={cx("wrapper", isCollapsed && "collapsed")}
            data-role="panel-collapsible-wrapper"
          >
            <Button
              icon={
                currentBreakpointWidth >= allBreakpoints.md
                  ? "caret-right"
                  : "caret-down"
              }
              onClick={() => handleOnCollapse(false)}
              className={cx("collapse-trigger")}
            />
            <div className={cx("title-container", "collapsed")}>
              <div
                className={cx(
                  "title",
                  "collapsed",
                  (!description || !subtitle) && "is-primary"
                )}
              >
                {title}
              </div>
              {description ? (
                <div className={cx("description", "collapsed")}>
                  {description}
                </div>
              ) : (
                description
              )}
              {isBusy ? (
                <IconLoading
                  speed="fast"
                  className={cx("loading-icon")}
                />
              ) : null}
            </div>
          </div>
        ) : (
          <div
            className={cx("wrapper")}
            data-role="panel-collapsible-wrapper"
          >
            <div
              className={cx("header")}
              data-role="panel-collapsible-header"
            >
              <div className={cx("header__container")}>
                <Button
                  icon={
                    currentBreakpointWidth >= allBreakpoints.md
                      ? "caret-left"
                      : "caret-up"
                  }
                  onClick={() => handleOnCollapse(true)}
                  className={cx("collapse-trigger")}
                />
                <div className={cx("header__title-container")}>
                  {subtitle ? (
                    <p className={cx("subtitle")}>{subtitle}</p>
                  ) : (
                    subtitle
                  )}
                  <p className={cx("title")}>
                    {title}
                    {titleDecorator}
                  </p>
                  {description ? (
                    <p className={cx("description")}>{description}</p>
                  ) : (
                    description
                  )}
                </div>
              </div>
              <div className={cx("header__actions-container")}>
                {isBusy ? <IconLoading speed="fast" /> : null}
                {actionsSlot}
              </div>
            </div>
            <div
              className={cx("scroll-area", canScrollAreaShrink && "can-shrink")}
              data-role="panel-collapsible-content"
            >
              {children}
            </div>
          </div>
        )}
      </div>
    </Skeleton>
  );
};
